<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search -->
            <div class="col-12 col-lg-3 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search reported websites..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" />
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Status:</span>
                  <select v-model="filterStatusBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in filterStatusList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Account:</span>
                  <select v-model="filterAccountBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="f in filterAccount" :value="f.value" :key="f.value" :title="f.text">{{ truncateText(f.text, 15) }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" style="width: 120px;">Status</th>
                     <th scope="col">Account</th>
                     <th scope="col">Website</th>
                     <th scope="col">Category</th>
                     <th scope="col" style="width: 100px;">Request</th>
                     <th scope="col" style="width: 160px;">Reported</th>
                     <th scope="col" style="width: 160px;">Actioned</th>
                     <th scope="col" style="width: 100px;">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td colspan="8">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(rw, index) in reportedWebsites" :key="index">
                     <td>
                        <span class="status-pill" :class="getStatusClass(rw)">
                           <i :class="getStatusIcon(rw)" class="me-1"></i>
                           {{ getStatusText(rw) }}
                        </span>
                     </td>
                     <td :title="rw.account ? rw.account.account_name : ''">
                        {{ rw.account ? truncateText(rw.account.account_name, 20) : '—' }}
                     </td>
                     <td :title="trimQuestionMark(rw.website)" style="font-family: monospace; font-size: 12px;">
                        {{ truncateText(trimQuestionMark(rw.website), 40) }}
                     </td>
                     <td>
                        <span class="category-text">{{ formatCategory(rw.category) }}</span>
                     </td>
                     <td>
                        <span class="request-pill" :class="getReportTypeClass(rw.report_type)">
                           {{ formatReportType(rw.report_type) }}
                        </span>
                     </td>
                     <td class="date-text">{{ formatDate(rw.date_created) }}</td>
                     <td class="date-text">
                        {{ (rw.date_actioned && !rw.date_actioned.startsWith('0001-01-01')) ? formatDate(rw.date_actioned) : '—' }}
                     </td>
                     <td>
                        <div class="d-flex gap-2" v-if="rw.is_marked === 0">
                           <button type="button" class="ms-action-btn ms-action-btn--success"
                              title="Mark as Safe" @click="markStatus(rw, 1)">
                              <i class="fa-solid fa-check"></i>
                           </button>
                           <button type="button" class="ms-action-btn ms-action-btn--delete"
                              title="Mark as Unsafe" @click="markStatus(rw, 0)">
                              <i class="fa-solid fa-xmark"></i>
                           </button>
                        </div>
                        <span v-else class="actioned-text">
                           <i class="fa-solid fa-circle-check me-1"></i> Done
                        </span>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>
</template>

<script>
import moment from 'moment'
import { debounce, extractErrorMessage } from '@/plugins/helper.js'

export default {
   data() {
      return {
         search: "",
         reportedWebsites: [],
         debounceFn: null,
         paginationData: {},
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         filterAccount: [
            { text: "All Accounts", value: "all_accounts" },
         ],
         filterAccountBy: "all_accounts",
         filterStatusList: [
            { text: "All Status", value: "all" },
            { text: "Pending", value: "pending" },
            { text: "Marked Safe", value: "safe" },
            { text: "Marked Unsafe", value: "unsafe" },
         ],
         filterStatusBy: "all",
         sortByList: [
            { text: "Date: Newest", value: "date_created__desc" },
            { text: "Date: Oldest", value: "date_created__asc" },
            { text: "Website: A-Z", value: "website__asc" },
            { text: "Website: Z-A", value: "website__desc" },
         ],
         sortBy: "date_created__desc",
         sorting: { field: "date_created", order: "desc" },
      }
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      filterByAccount() { return this.filterAccountBy },
      filterByStatus() { return this.filterStatusBy },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getReportedWebsites() },
      filterByAccount() { this.getReportedWebsites() },
      filterByStatus() { this.pagination.page = 1; this.getReportedWebsites() },
      rowsPerPage() { this.pagination.page = 1; this.getReportedWebsites() },
      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getReportedWebsites()
      },
   },

   created() {
      this.debounceFn = debounce(() => this.getReportedWebsites(), 500)
      this.init()
   },

   methods: {
      init() {
         this.getReportedWebsites()
         let orgs = this.$store.state.auth.organisations
         if (orgs && orgs.length > 0) {
            for (let x = 0; x < orgs.length; x++) {
               this.filterAccount.push({ text: orgs[x].name, value: orgs[x].orguuid })
            }
         }
      },

      formatDate(date) {
         if (!date) return '';
         return moment(date).format("DD-MMM-YYYY, hh:mm A")
      },

      formatReportType(type) {
         if (type === 1) return 'Allow';
         if (type === 0) return 'Block';
         return 'N/A';
      },

      getReportTypeClass(type) {
         if (type === 1) return 'request-allow';
         if (type === 0) return 'request-block';
         return 'request-default';
      },

      formatCategory(value) {
         if (!value || value === 'NONE' || value === 'null') return 'N/A';
         return value;
      },

      getStatusClass(website) {
         if (website.is_marked === 0) return 'status-pending';
         if (website.is_safe >= 1) return 'status-safe';
         return 'status-unsafe';
      },

      getStatusText(website) {
         if (website.is_marked === 0) return 'Pending';
         if (website.is_safe >= 1) return 'Safe';
         return 'Unsafe';
      },

      getStatusIcon(website) {
         if (website.is_marked === 0) return 'fa-solid fa-clock';
         if (website.is_safe >= 1) return 'fa-solid fa-circle-check';
         return 'fa-solid fa-circle-xmark';
      },

      truncateText(text, limit) {
         if (!text) return '';
         return text.length <= limit ? text : text.slice(0, limit) + '...';
      },

      trimQuestionMark(text) {
         if (!text) return '';
         let index = text.indexOf('?');
         return index > 0 ? text.substring(0, index) : text;
      },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      async markStatus(website, is_safe) {
         try {
            const payload = { id: website.id, domain: website.website, is_safe: is_safe }
            const resp = await this.$axios.post("admin-controls/mark-reported-website", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getReportedWebsites()
            }
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to update reported website status."))
            this.getReportedWebsites()
         }
      },

      async getReportedWebsites() {
         let apiPath = "admin-controls/get-reported-websites?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page + "&organisation=" + this.filterAccountBy

         if (this.filterStatusBy !== 'all') {
            apiPath += "&status=" + this.filterStatusBy
         }
         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&order_by=" + this.sorting.order
            apiPath += "&field=" + this.sorting.field
         }
         if (this.search !== "" || this.search !== null) {
            apiPath += "&search=" + this.search
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.account_reported_websites === "No Record Found")
                  this.reportedWebsites = []
               else {
                  this.reportedWebsites = response.data.reported_websites
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               this.$toast.error(extractErrorMessage(error, "Failed to load reported websites."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.date-text { font-size: 13px; color: #6c757d; white-space: nowrap; }
.category-text { font-size: 13px; color: #435666; }

/* Status pills */
.status-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}
.status-pending { background: #fff8e1; color: #e6a100; }
.status-safe { background: #e6f9ee; color: #00873c; }
.status-unsafe { background: #fde8ea; color: #c62828; }

/* Request type pills */
.request-pill {
   padding: 3px 8px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}
.request-allow { background: #e6f9ee; color: #00873c; }
.request-block { background: #fde8ea; color: #c62828; }
.request-default { background: #f1f3f5; color: #6c757d; }

/* Action buttons */
.btn-action {
   width: 30px; height: 30px; padding: 0;
   border-radius: 6px;
   display: inline-flex; align-items: center; justify-content: center;
   font-size: 12px; transition: all 0.15s ease;
}
.btn-action-safe { border: 1px solid #00873c; color: #00873c; background: #fff; }
.btn-action-safe:hover { background: #00873c; color: #fff; }
.btn-action-unsafe { border: 1px solid #c62828; color: #c62828; background: #fff; }
.btn-action-unsafe:hover { background: #c62828; color: #fff; }

.actioned-text { font-size: 12px; color: #adb5bd; font-weight: 500; }
</style>